/**
 * Find in Store - StoreAvailability API Implementation
 * 
 * This implementation uses Shopify's StoreAvailability API to determine store availability.
 * If the API is accessible and returns location data, pickup functionality is automatically enabled.
 * 
 * Key Features:
 * - Uses StoreAvailability API (Storefront API) as primary method
 * - If API returns locations, pickup is enabled by default for those locations
 * - No dependency on Shopify Admin "Pickup in Store" settings
 * - No dependency on manual enable_find_in_store field in metaobjects
 * - Falls back to inventory-based method if StoreAvailability API is unavailable
 * 
 * Requirements:
 * - Storefront API access token must be configured
 * - Store metaobjects must have location_id field matching Shopify locations
 * - Stores data must be embedded on page via find-in-store-block.liquid
 * 
 * How It Works:
 * - StoreAvailability API accessible + returns locations = Pickup enabled (automatic)
 * - Each location in API response is treated as pickup-enabled for that variant
 * 
 * @version 3.4
 * @updated 2026-04-09
 */

(function() {
  'use strict';

  // ========================================
  // Constants & Configuration
  // ========================================
  const STORAGE_KEY = 'fis_selected_store';
  const STORAGE_EXPIRY_DAYS = 30;
  
  // Default thresholds - can be overridden by section settings
  let INVENTORY_THRESHOLDS = {
    IN_STOCK: 3,
    LOW_STOCK: 1,
    OUT_OF_STOCK: 0
  };

  // Default messages - can be overridden by section settings
  let INVENTORY_MESSAGES = {
    IN_STOCK: 'In Stock',
    LOW_STOCK: 'Limited Stock',
    OUT_OF_STOCK: 'Out of Stock'
  };

  const DEFAULT_STORES_TO_SHOW = 3;

  // ========================================
  // Utility Functions
  // ========================================
  
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(1);
  }

  function getUserLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }),
        (error) => reject(error),
        { timeout: 10000, enableHighAccuracy: false }
      );
    });
  }

  function setStorageWithExpiry(key, value, expiryDays) {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + (expiryDays * 24 * 60 * 60 * 1000)
    };
    try {
      localStorage.setItem(key, JSON.stringify(item));
    } catch (e) {
      console.warn('[Find in Store] localStorage not available', e);
    }
  }

  function getStorageWithExpiry(key) {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      
      const item = JSON.parse(itemStr);
      const now = new Date();
      
      if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      
      return item.value;
    } catch (e) {
      console.warn('[Find in Store] Error reading from localStorage', e);
      return null;
    }
  }

  function getInventoryStatus(quantity) {
    if (quantity >= INVENTORY_THRESHOLDS.IN_STOCK) {
      return { status: 'in-stock', label: INVENTORY_MESSAGES.IN_STOCK };
    } else if (quantity >= INVENTORY_THRESHOLDS.LOW_STOCK) {
      return { status: 'low-stock', label: INVENTORY_MESSAGES.LOW_STOCK };
    } else {
      return { status: 'out-of-stock', label: INVENTORY_MESSAGES.OUT_OF_STOCK };
    }
  }

  /**
   * Load threshold and message settings from section data attributes
   */
  function loadSettingsFromSection(section) {
    if (!section) return;

    const inStockThreshold = section.dataset.fisInStockThreshold;
    const lowStockThreshold = section.dataset.fisLowStockThreshold;
    const inStockMessage = section.dataset.fisInStockMessage;
    const limitedStockMessage = section.dataset.fisLimitedStockMessage;
    const outOfStockMessage = section.dataset.fisOutOfStockMessage;

    if (inStockThreshold) {
      INVENTORY_THRESHOLDS.IN_STOCK = parseInt(inStockThreshold, 10);
    }
    if (lowStockThreshold) {
      INVENTORY_THRESHOLDS.LOW_STOCK = parseInt(lowStockThreshold, 10);
    }
    if (inStockMessage) {
      INVENTORY_MESSAGES.IN_STOCK = inStockMessage;
    }
    if (limitedStockMessage) {
      INVENTORY_MESSAGES.LOW_STOCK = limitedStockMessage;
    }
    if (outOfStockMessage) {
      INVENTORY_MESSAGES.OUT_OF_STOCK = outOfStockMessage;
    }

    console.log('[Find in Store] Loaded settings - Thresholds:', INVENTORY_THRESHOLDS, 'Messages:', INVENTORY_MESSAGES);
  }

  // ========================================
  // API Functions - Storefront API with proper context
  // ========================================
  
  async function storefrontQuery(query, variables = {}) {
    const apiUrl = `${window.Shopify.routes.root}api/2024-01/graphql.json`;
    
    // Diagnostic logging
    console.log('[Find in Store] 🔍 Checking Storefront API configuration...');
    console.log('[Find in Store] API URL:', apiUrl);
    console.log('[Find in Store] Token configured:', !!window.Shopify?.storefrontAccessToken);
    
    // Check if we have a token
    if (!window.Shopify?.storefrontAccessToken) {
      console.warn('[Find in Store] ❌ No Storefront API token found');
      console.warn('[Find in Store] → window.Shopify.storefrontAccessToken is:', window.Shopify?.storefrontAccessToken);
      console.warn('[Find in Store] → To fix: Ensure Storefront API token is set in theme');
      throw new Error('No storefront token');
    }
    
    console.log('[Find in Store] ✅ Token found, making API request...');
    
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken
        },
        body: JSON.stringify({ query, variables })
      });
      
      console.log('[Find in Store] API Response status:', response.status);
      
      if (!response.ok) {
        console.error('[Find in Store] ❌ API request failed with status:', response.status);
        throw new Error(`API request failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.errors) {
        console.error('[Find in Store] ❌ GraphQL errors:', data.errors);
        throw new Error(data.errors[0]?.message || 'GraphQL error');
      }
      
      console.log('[Find in Store] ✅ API request successful');
      return data.data;
    } catch (error) {
      console.error('[Find in Store] ❌ API query failed:', error);
      throw error;
    }
  }

  /**
   * Diagnostic function to test StoreAvailability API access
   * Can be called from browser console: window.testStoreAvailabilityAPI()
   */
  window.testStoreAvailabilityAPI = async function(variantId) {
    console.log('='.repeat(60));
    console.log('🔍 STORE AVAILABILITY API DIAGNOSTIC TEST');
    console.log('='.repeat(60));
    
    // Step 1: Check Shopify object
    console.log('\n📋 Step 1: Checking Shopify configuration...');
    console.log('  window.Shopify exists:', !!window.Shopify);
    console.log('  window.Shopify.routes:', window.Shopify?.routes);
    console.log('  storefrontAccessToken:', window.Shopify?.storefrontAccessToken ? '✅ SET' : '❌ NOT SET');
    
    if (!window.Shopify?.storefrontAccessToken) {
      console.error('\n❌ PROBLEM: Storefront API token is not configured!');
      console.log('\n🔧 TO FIX: Add this to your theme (theme.liquid or a snippet):');
      console.log(`
  <script>
    window.Shopify = window.Shopify || {};
    window.Shopify.storefrontAccessToken = 'YOUR_STOREFRONT_API_TOKEN';
  </script>
      `);
      console.log('\nYou can get the token from:');
      console.log('  Shopify Admin → Settings → Apps and sales channels → Develop apps');
      console.log('  → Create or select an app → API credentials → Storefront API access token');
      return { success: false, error: 'No token' };
    }
    
    // Step 2: Test basic API access
    console.log('\n📋 Step 2: Testing basic API access...');
    const apiUrl = `${window.Shopify.routes.root}api/2024-01/graphql.json`;
    console.log('  API URL:', apiUrl);
    
    try {
      const basicQuery = `{ shop { name } }`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken
        },
        body: JSON.stringify({ query: basicQuery })
      });
      
      console.log('  Response status:', response.status);
      
      if (!response.ok) {
        console.error('  ❌ API returned error status:', response.status);
        const text = await response.text();
        console.error('  Response body:', text);
        return { success: false, error: `HTTP ${response.status}` };
      }
      
      const data = await response.json();
      console.log('  ✅ Basic API access works!');
      console.log('  Shop name:', data.data?.shop?.name);
      
    } catch (error) {
      console.error('  ❌ API request failed:', error);
      return { success: false, error: error.message };
    }
    
    // Step 3: Test StoreAvailability query
    console.log('\n📋 Step 3: Testing StoreAvailability query...');
    
    // Find a variant ID if not provided
    if (!variantId) {
      const variantInput = document.querySelector('[name="id"], .product-variant-id, [data-product-variant-id]');
      variantId = variantInput?.value;
      console.log('  Auto-detected variant ID:', variantId);
    }
    
    if (!variantId) {
      console.warn('  ⚠️ No variant ID found. Please provide one:');
      console.log('  window.testStoreAvailabilityAPI("YOUR_VARIANT_ID")');
      return { success: false, error: 'No variant ID' };
    }
    
    const gid = variantId.toString().startsWith('gid://') 
      ? variantId 
      : `gid://shopify/ProductVariant/${variantId}`;
    
    console.log('  Variant GID:', gid);
    
    try {
      const storeAvailQuery = `
        query GetVariantStoreAvailability($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              id
              title
              availableForSale
              quantityAvailable
              storeAvailability(first: 20) {
                edges {
                  node {
                    available
                    pickUpTime
                    quantityAvailable
                    location {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': window.Shopify.storefrontAccessToken
        },
        body: JSON.stringify({ query: storeAvailQuery, variables: { id: gid } })
      });
      
      const data = await response.json();
      
      if (data.errors) {
        console.error('  ❌ GraphQL errors:', data.errors);
        return { success: false, error: data.errors[0]?.message };
      }
      
      const storeAvail = data.data?.node?.storeAvailability?.edges || [];
      const variantQuantity = data.data?.node?.quantityAvailable;
      
      console.log('  ✅ StoreAvailability query successful!');
      console.log('  Variant title:', data.data?.node?.title);
      console.log('  Available for sale:', data.data?.node?.availableForSale);
      console.log('  Variant total quantityAvailable:', variantQuantity !== undefined ? variantQuantity : '❌ NOT AVAILABLE IN API');
      console.log('  Store locations with availability:', storeAvail.length);
      
      // Check if per-location quantity is available
      const hasPerLocationQty = storeAvail.some(edge => edge.node.quantityAvailable !== undefined);
      console.log('  Per-location quantityAvailable:', hasPerLocationQty ? '✅ AVAILABLE' : '❌ NOT AVAILABLE IN API');
      
      if (storeAvail.length > 0) {
        console.log('\n📍 Available Locations:');
        storeAvail.forEach((edge, i) => {
          const loc = edge.node;
          console.log(`  ${i + 1}. ${loc.location.name}`);
          console.log(`     ID: ${loc.location.id}`);
          console.log(`     Available: ${loc.available ? '✅ Yes' : '❌ No'}`);
          console.log(`     Pickup Time: ${loc.pickUpTime || 'N/A'}`);
          if (loc.quantityAvailable !== undefined) {
            console.log(`     Quantity Available: ${loc.quantityAvailable}`);
          }
        });
      } else {
        console.log('\n⚠️ No store locations returned.');
        console.log('   This could mean:');
        console.log('   - No inventory at any location for this variant');
        console.log('   - StoreAvailability is not configured for this store');
      }
      
      console.log('\n' + '='.repeat(60));
      console.log('✅ DIAGNOSTIC TEST COMPLETE');
      console.log('='.repeat(60));
      
      return { 
        success: true, 
        data: data.data,
        locationsCount: storeAvail.length,
        hasPerLocationQuantity: hasPerLocationQty,
        variantQuantity: variantQuantity
      };
      
    } catch (error) {
      console.error('  ❌ StoreAvailability query failed:', error);
      return { success: false, error: error.message };
    }
  };
  
  console.log('[Find in Store] 💡 Diagnostic tool available: Run window.testStoreAvailabilityAPI() in console');

  /**
   * Fetch store availability for a specific variant using StoreAvailability API
   * 
   * If this API is accessible and returns location data, it means:
   * - Pickup functionality is available (StoreAvailability enabled)
   * - No need to check Shopify Admin "Pickup in Store" settings
   * - Locations in the response have pickup enabled automatically
   * 
   * The API returns which locations have this specific variant available for pickup.
   */
  async function fetchStoreAvailabilityForVariant(variantId) {
    console.log('[Find in Store] Fetching StoreAvailability for variant:', variantId);
    console.log('[Find in Store] 💡 Run window.testStoreAvailabilityAPI() for detailed diagnostics');
    
    try {
      const query = `
        query GetVariantStoreAvailability($id: ID!) {
          node(id: $id) {
            ... on ProductVariant {
              id
              title
              availableForSale
              quantityAvailable
              storeAvailability(first: 20) {
                edges {
                  node {
                    available
                    pickUpTime
                    quantityAvailable
                    location {
                      id
                      name
                      address {
                        address1
                        address2
                        city
                        province
                        country
                        zip
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      // Convert Shopify variant ID to GID format if needed
      const gid = variantId.toString().startsWith('gid://') 
        ? variantId 
        : `gid://shopify/ProductVariant/${variantId}`;
      
      const data = await storefrontQuery(query, { id: gid });
      
      if (!data.node || !data.node.storeAvailability) {
        console.warn('[Find in Store] No store availability data returned for variant:', variantId);
        return { enabled: false, locations: [] };
      }
      
      const storeAvailability = data.node.storeAvailability.edges.map(edge => edge.node);
      
      console.log('[Find in Store] ✅ StoreAvailability API is ENABLED - returned', storeAvailability.length, 'locations');
      console.log('[Find in Store] → This means pickup functionality is automatically available');
      
      // Extract location IDs from the GID format (gid://shopify/Location/12345 -> 12345)
      // Also capture quantityAvailable if the API returns it
      const locationsData = storeAvailability.map(node => {
        const locationIdMatch = node.location.id.match(/Location\/(\d+)/);
        const locationId = locationIdMatch ? locationIdMatch[1] : null;
        
        // Log if quantityAvailable is present in the API response
        if (node.quantityAvailable !== undefined) {
          console.log(`[FIS] API returned quantityAvailable for ${node.location.name}: ${node.quantityAvailable}`);
        }
        
        return {
          locationId: locationId,
          locationGid: node.location.id,
          locationName: node.location.name,
          available: node.available,
          pickUpTime: node.pickUpTime,
          address: node.location.address,
          quantityAvailable: node.quantityAvailable // Will be undefined if not supported by API
        };
      });
      
      // Get variant-level quantityAvailable if present
      const variantQuantity = data.node.quantityAvailable;
      if (variantQuantity !== undefined) {
        console.log('[Find in Store] Variant total quantityAvailable:', variantQuantity);
      }
      
      return {
        enabled: storeAvailability.length > 0, // If API returns locations, pickup is enabled
        locations: locationsData,
        variantQuantityAvailable: variantQuantity,
        _apiSource: 'storeAvailability'
      };
    } catch (error) {
      console.error('[Find in Store] ❌ StoreAvailability API failed:', error.message);
      console.log('[Find in Store] → API not accessible, will use fallback method');
      console.log('[Find in Store] 💡 Run window.testStoreAvailabilityAPI() in console for detailed diagnostics');
      
      // Provide specific guidance based on error type
      if (error.message === 'No storefront token') {
        console.warn('[Find in Store] ⚠️ MISSING: window.Shopify.storefrontAccessToken');
        console.warn('[Find in Store] → Add the token to your theme (see diagnostic tool for instructions)');
      }
      
      return { enabled: false, locations: [], _error: error.message };
    }
  }

  /**
   * Fetch stores from Metaobjects using Storefront API
   */
  async function fetchStores() {
    // First, try to get stores from embedded data on the page
    const embeddedStores = getEmbeddedStoresData();
    if (embeddedStores && embeddedStores.length > 0) {
      console.log('[Find in Store] Using embedded stores data:', embeddedStores.length);
      return embeddedStores;
    }
    
    // Fallback: Create a default store entry based on the inventory location
    // This works when all stores share the same Shopify location for inventory
    console.log('[Find in Store] Using default store fallback');
    const defaultStore = createDefaultStore();
    if (defaultStore) {
      return [defaultStore];
    }
    
    // Last resort: Try GraphQL
    try {
      return await fetchStoresViaGraphQL();
    } catch (error) {
      console.log('[Find in Store] GraphQL failed, using empty stores:', error.message);
      return [];
    }
  }
  
  /**
   * Create a default store when no stores data is available
   * This is used when all stores share the same location ID
   */
  function createDefaultStore() {
    // Look for inventory data to get the location ID
    const inventoryEl = document.querySelector('[data-fis-variants-inventory]');
    if (!inventoryEl) return null;
    
    try {
      const inventoryData = JSON.parse(inventoryEl.textContent || '{}');
      const firstVariantId = Object.keys(inventoryData)[0];
      if (!firstVariantId) return null;
      
      const variantData = inventoryData[firstVariantId];
      const storeAvailabilities = variantData?.store_availabilities || {};
      const locationIds = Object.keys(storeAvailabilities);
      
      if (locationIds.length === 0) return null;
      
      // Get the first available location
      const locationId = locationIds[0];
      const locationData = storeAvailabilities[locationId];
      
      console.log('[Find in Store] Creating default store from location:', locationId, locationData?.location_name);
      
      return {
        id: `default-${locationId}`,
        handle: 'default-store',
        name: locationData?.location_name || 'Store Pickup Available',
        description: '',
        address: '',
        city: '',
        region: '',
        country: 'CA',
        postal_code: '',
        latitude: null,
        longitude: null,
        phone: '',
        email: '',
        store_type: 'retail',
        hours_json: '{}',
        timezone: '',
        enable_find_in_store: true,
        location_id: locationId,
        image: null
      };
    } catch (error) {
      console.error('[Find in Store] Error creating default store:', error);
      return null;
    }
  }
  
  /**
   * Get stores data from embedded JSON on the page
   * Updated: No longer filters by enable_find_in_store - StoreAvailability API determines availability
   */
  function getEmbeddedStoresData() {
    const dataEl = document.querySelector('[data-stores-json]');
    if (!dataEl) {
      console.log('[Find in Store] No embedded stores data found');
      return null;
    }
    
    try {
      const storesData = JSON.parse(dataEl.textContent || '[]');
      console.log('[Find in Store] Found', storesData.length, 'stores in embedded data');
      
      // Filter stores that have a location ID (we need this to match with StoreAvailability API)
      // No longer checking enable_find_in_store - the API will tell us which locations have pickup
      const filteredStores = storesData.filter(store => {
        const hasLocationId = store.location_id && String(store.location_id).trim() !== '';
        
        if (!hasLocationId) {
          console.log('[Find in Store] Skipping store without location_id:', store.name);
        }
        
        return hasLocationId;
      });
      
      console.log('[Find in Store] Filtered to', filteredStores.length, 'stores with location_id');
      
      if (filteredStores.length === 0 && storesData.length > 0) {
        console.warn('[Find in Store] All stores filtered out - missing location_id field');
      }
      
      return filteredStores;
    } catch (error) {
      console.error('[Find in Store] Error parsing embedded stores data:', error);
      return null;
    }
  }

  /**
   * Fetch stores via GraphQL (requires Storefront API token)
   */
  async function fetchStoresViaGraphQL() {
    const query = `
      query GetStoreMetaobjects($type: String!, $first: Int!) {
        metaobjects(type: $type, first: $first) {
          edges {
            node {
              id
              handle
              fields {
                key
                value
                type
                reference {
                  ... on MediaImage {
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    
    const data = await storefrontQuery(query, { type: 'stores', first: 100 });
    
    if (!data.metaobjects || !data.metaobjects.edges) {
      console.warn('[Find in Store] No metaobjects returned');
      return [];
    }
    
    return parseStoresFromData(data.metaobjects.edges);
  }

  /**
   * Parse stores from GraphQL response
   * Updated: No longer filters by enable_find_in_store - StoreAvailability API determines availability
   */
  function parseStoresFromData(edges) {
    try {
      const stores = edges.map(edge => {
        const fields = {};
        edge.node.fields.forEach(field => {
          if (field.type === 'file_reference' && field.reference?.image) {
            fields[field.key] = field.reference.image.url;
          } else {
            fields[field.key] = field.value;
          }
        });
        
        // Handle different possible field names for location_id
        const locationId = fields.location_id || 
                          fields.shopify_location_id || 
                          fields['location-id'] || 
                          fields['Location ID'] || 
                          '';
        
        return {
          id: edge.node.id,
          handle: edge.node.handle,
          name: fields.name || '',
          description: fields.description || '',
          address: fields.address || '',
          city: fields.city || '',
          region: fields.region || '',
          country: fields.country || '',
          postal_code: fields.postal_code || '',
          latitude: parseFloat(fields.latitude) || null,
          longitude: parseFloat(fields.longitude) || null,
          phone: fields.phone || '',
          email: fields.email || '',
          store_type: fields.store_type || '',
          hours_json: fields.hours_json || '{}',
          timezone: fields.timezone || '',
          location_id: locationId,
          image: fields.image || null,
          google_my_business_map_link: fields.google_my_business_map_link || '',
          _rawFields: fields // Keep raw fields for debugging
        };
      });
      
      // Debug logging
      console.log('[Find in Store] Total stores fetched:', stores.length);
      console.log('[Find in Store] Stores with location_id:', 
        stores.filter(s => s.location_id).length);
      
      if (stores.length > 0) {
        console.log('[Find in Store] First store sample:', stores[0]);
        console.log('[Find in Store] Available field keys:', Object.keys(stores[0]._rawFields));
      }
      
      // Only filter by location_id - StoreAvailability API will determine which have pickup enabled
      const filteredStores = stores.filter(store => store.location_id);
      console.log('[Find in Store] Stores after filtering (location_id only):', filteredStores.length);
      
      if (filteredStores.length === 0 && stores.length > 0) {
        console.warn('[Find in Store] All stores filtered out. Check:');
        console.warn('  - location_id field values:', stores.map(s => ({ name: s.name, location: s._rawFields.location_id || 'MISSING' })));
      }
      
      return filteredStores;
    } catch (error) {
      console.error('[Find in Store] Failed to parse stores:', error);
      return [];
    }
  }

  /**
   * Fetch inventory availability using variant endpoint
   * This uses Shopify's section rendering to get availability data
   */
  async function fetchInventoryForVariant(variantId) {
    try {
      // Use Shopify's section rendering API to get availability
      const url = `${window.Shopify.routes.root}variants/${variantId}/?section_id=find-in-store-availability`;
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch variant data: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const dataEl = doc.querySelector('[data-fis-inventory-data]');
      
      if (dataEl) {
        return JSON.parse(dataEl.textContent || '{}');
      }
      
      return {};
    } catch (error) {
      console.error('[Find in Store] Failed to fetch inventory:', error);
      return {};
    }
  }

  /**
   * Alternative: Fetch via custom endpoint if you create one
   */
  async function fetchInventoryViaEndpoint(productHandle, variantId) {
    try {
      const url = `/apps/store-inventory?product=${productHandle}&variant=${variantId}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Endpoint failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('[Find in Store] Endpoint error:', error);
      return {};
    }
  }

  // ========================================
  // Store Filtering & Sorting
  // ========================================
  
  function filterStoresWithInventory(stores, inventoryData) {
    // Handle fallback case: variant is available but no store_availabilities
    // Don't show all stores - this causes incorrect results
    if (inventoryData._fallback && inventoryData._available) {
      console.log('[Find in Store] Fallback mode: No specific location data available');
      console.log('[Find in Store] Cannot determine which stores have inventory');
      return [];
    }
    
    // Handle empty/error case
    if (inventoryData._empty || inventoryData._error) {
      console.log('[Find in Store] No inventory data, returning empty list');
      return [];
    }
    
    // Normal case: filter by location-specific inventory
    console.log('[Find in Store] Filtering stores by inventory map:', inventoryData);
    console.log('[Find in Store] Available location IDs:', Object.keys(inventoryData));
    
    const storesWithInventory = stores
      .map(store => {
        // Normalize location_id to string for comparison (Shopify IDs can be strings or numbers)
        const locationId = String(store.location_id || '').trim();
        const quantity = inventoryData[locationId] || 0;
        const inventoryStatus = getInventoryStatus(quantity);
        
        console.log(`[FIS] Store: ${store.name}, Location ID: "${locationId}", Quantity: ${quantity}`);
        
        // Only include if quantity is above out-of-stock threshold
        if (quantity > INVENTORY_THRESHOLDS.OUT_OF_STOCK) {
          return {
            ...store,
            quantity: quantity,
            available: true,
            status: inventoryStatus.status,
            statusLabel: inventoryStatus.label
          };
        }
        
        return null;
      })
      .filter(Boolean);
    
    console.log('[Find in Store] Filtered to', storesWithInventory.length, 'stores with inventory');
    return storesWithInventory;
  }

  /**
   * Filter stores using StoreAvailability API data
   * 
   * Logic:
   * - If StoreAvailability API is accessible (enabled=true), pickup is available
   * - Each location in the API response has pickup enabled automatically
   * - We match API locations to store metaobjects by location_id
   * - Only show stores where the specific variant is available (available=true in API)
   */
  function filterStoresWithStoreAvailability(stores, storeAvailabilityData, inventoryData = {}) {
    // If API is not enabled or failed, return empty
    if (!storeAvailabilityData.enabled) {
      console.log('[Find in Store] StoreAvailability API not accessible');
      if (storeAvailabilityData._error) {
        console.log('[Find in Store] API Error:', storeAvailabilityData._error);
      }
      return [];
    }

    const locations = storeAvailabilityData.locations || [];
    console.log('[Find in Store] ✅ StoreAvailability API is ENABLED');
    console.log('[Find in Store] → Pickup functionality is automatically available');
    console.log('[Find in Store] → API returned', locations.length, 'locations with this variant');
    
    // Check if API returned quantityAvailable (not all Shopify versions support this)
    const apiHasQuantity = locations.some(loc => loc.quantityAvailable !== undefined && loc.quantityAvailable !== null);
    console.log('[Find in Store] API has per-location quantityAvailable:', apiHasQuantity);
    
    // Create a map of location_id to availability data
    const locationMap = {};
    locations.forEach(loc => {
      if (loc.locationId) {
        locationMap[loc.locationId] = loc;
      }
    });
    
    console.log('[Find in Store] Location IDs from API:', Object.keys(locationMap));
    console.log('[Find in Store] Inventory data for quantity lookup:', inventoryData);
    
    // Check if inventoryData is valid (not a fallback/error object)
    const hasValidInventoryData = inventoryData && 
      !inventoryData._fallback && 
      !inventoryData._empty && 
      !inventoryData._error &&
      Object.keys(inventoryData).some(key => !key.startsWith('_'));
    
    console.log('[Find in Store] Has valid inventory data:', hasValidInventoryData);
    
    const storesWithAvailability = stores
      .map(store => {
        // Normalize location_id to string for comparison
        const locationId = String(store.location_id || '').trim();
        const availabilityData = locationMap[locationId];
        
        console.log(`[FIS] Checking store: ${store.name}, Location ID: "${locationId}", API has data: ${!!availabilityData}`);
        
        // Only include stores that appear in StoreAvailability API and are available
        if (availabilityData && availabilityData.available) {
          let quantity;
          let inventoryStatus;
          
          // Priority 1: Use quantityAvailable from API if available
          if (apiHasQuantity && availabilityData.quantityAvailable !== undefined && availabilityData.quantityAvailable !== null) {
            quantity = availabilityData.quantityAvailable;
            inventoryStatus = getInventoryStatus(quantity);
            console.log(`[FIS] Using API quantityAvailable for ${store.name}: ${quantity}`);
          }
          // Priority 2: Use embedded inventory data
          else if (hasValidInventoryData && inventoryData[locationId] !== undefined) {
            quantity = inventoryData[locationId];
            inventoryStatus = getInventoryStatus(quantity);
            console.log(`[FIS] Using embedded inventory for ${store.name}: ${quantity}`);
          }
          // Priority 3: Default to "In Stock" since API confirms available
          else {
            quantity = INVENTORY_THRESHOLDS.IN_STOCK;
            inventoryStatus = { status: 'in-stock', label: INVENTORY_MESSAGES.IN_STOCK };
            console.log(`[FIS] Defaulting to In Stock for ${store.name} (API confirms available)`);
          }
          
          console.log(`[FIS] ✅ Store ${store.name} - variant available for pickup, quantity: ${quantity}, status: ${inventoryStatus.label}`);
          return {
            ...store,
            available: true,
            quantity: quantity,
            pickUpTime: availabilityData.pickUpTime,
            status: inventoryStatus.status,
            statusLabel: inventoryStatus.label,
            _apiLocationData: availabilityData
          };
        }
        
        if (availabilityData && !availabilityData.available) {
          console.log(`[FIS] ❌ Store ${store.name} - location exists but variant not available`);
        } else {
          console.log(`[FIS] ❌ Store ${store.name} - not in API response`);
        }
        return null;
      })
      .filter(Boolean);
    
    console.log('[Find in Store] Filtered to', storesWithAvailability.length, 'stores with pickup availability');
    return storesWithAvailability;
  }

  function sortStoresByDistance(stores, userLocation) {
    if (!userLocation) return stores;
    
    return stores
      .map(store => ({
        ...store,
        distance: store.latitude && store.longitude
          ? calculateDistance(
              userLocation.lat,
              userLocation.lng,
              store.latitude,
              store.longitude
            )
          : null
      }))
      .sort((a, b) => {
        if (a.distance === null && b.distance === null) return 0;
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return parseFloat(a.distance) - parseFloat(b.distance);
      });
  }

  function filterStoresBySearch(stores, searchQuery) {
    if (!searchQuery) return stores;
    
    const query = searchQuery.toLowerCase().trim();
    return stores.filter(store => {
      return (
        store.name.toLowerCase().includes(query) ||
        store.city.toLowerCase().includes(query) ||
        store.postal_code.toLowerCase().includes(query) ||
        store.address.toLowerCase().includes(query)
      );
    });
  }

  function filterStoresByState(stores, stateCode) {
    if (!stateCode) return stores;
    return stores.filter(store => 
      store.region.toUpperCase() === stateCode.toUpperCase()
    );
  }

  // ========================================
  // UI Rendering Functions
  // ========================================
  
  function renderStoreList(modal, stores, selectedStoreId) {
    const storeList = modal.querySelector('[data-fis-store-list]');
    const template = modal.querySelector('[data-fis-store-template]');
    const countEl = modal.querySelector('[data-fis-count-text]');
    const emptyEl = modal.querySelector('[data-fis-empty]');
    const loadingEl = modal.querySelector('[data-fis-loading]');
    
    if (!storeList || !template) return;
    
    storeList.innerHTML = '';
    loadingEl?.classList.add('fis-hidden');
    
    if (stores.length === 0) {
      emptyEl?.classList.remove('fis-hidden');
      countEl.textContent = '';
      return;
    } else {
      emptyEl?.classList.add('fis-hidden');
    }
    
    countEl.textContent = `(${stores.length})`;
    
    stores.forEach(store => {
      const clone = template.content.cloneNode(true);
      const item = clone.querySelector('.fis-store-item');
      
      if (!item) return;
      
      item.dataset.storeId = store.id;
      
      if (selectedStoreId && store.id === selectedStoreId) {
        item.classList.add('selected');
        item.querySelector('[data-fis-select]').textContent = 'Selected';
      }
      
      const nameEl = clone.querySelector('.fis-store-name');
      const cityEl = clone.querySelector('.fis-store-city');
      const statusEl = clone.querySelector('.fis-store-status');
      const addressEl = clone.querySelector('.fis-store-address');
      const postalcodeEl = clone.querySelector('.fis-store-postalcode');
      const distanceEl = clone.querySelector('.fis-store-distance');
      const phoneEl = clone.querySelector('.fis-store-phone');
      const hoursEl = clone.querySelector('.fis-store-hours');
      
      if (nameEl) nameEl.textContent = store.name;
      if (cityEl) cityEl.textContent = `${store.city}${store.region ? ', ' + store.region : ''}`;
      
      if (statusEl) {
        statusEl.textContent = store.statusLabel || 'Available';
        statusEl.className = `fis-store-status ${store.status || 'in-stock'}`;
      }
      
      if (addressEl) addressEl.textContent = store.address;
      if (postalcodeEl) postalcodeEl.textContent = store.postal_code;
      
      if (distanceEl && store.distance) {
        distanceEl.textContent = `${store.distance} miles away`;
      } else if (distanceEl) {
        distanceEl.style.display = 'none';
      }
      
      if (phoneEl && store.phone) {
        phoneEl.innerHTML = `<span class="phone-label">Phone Number:</span> <a href="tel:${store.phone}">${store.phone}</a>`;
      } else if (phoneEl) {
        phoneEl.style.display = 'none';
      }
      
      if (hoursEl) {
        try {
          // hours_json might already be an object or a string
          const hours = typeof store.hours_json === 'string' 
            ? JSON.parse(store.hours_json || '{}') 
            : (store.hours_json || {});
          
          const daysMap = { 'sun': 'Sunday', 'mon': 'Monday', 'tue': 'Tuesday', 'wed': 'Wednesday', 'thu': 'Thursday', 'fri': 'Friday', 'sat': 'Saturday' };
          const today = new Date().toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase().slice(0, 3);
          
          if (Object.keys(hours).length > 0) {
            const todayHours = hours[today];
            
            if (todayHours && todayHours.length > 0) {
              // Format today's hours from array of {open, close} objects
              const timeRanges = todayHours.map(h => `${h.open} - ${h.close}`).join(', ');
              hoursEl.innerHTML = `<div class="hours-today"><span>Today's Opening Hours:</span> <span>${timeRanges}</span></div>`;
            } else {
              // Show full week schedule
              const dayOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
              let scheduleHTML = '<div class="hours-week">';
              dayOrder.forEach(day => {
                if (hours[day] && hours[day].length > 0) {
                  const timeRanges = hours[day].map(h => `${h.open} - ${h.close}`).join(', ');
                  const dayClass = day === today ? 'hours-row today' : 'hours-row';
                  scheduleHTML += `<div class="${dayClass}"><span class="hours-day">${daysMap[day]}:</span> <span class="hours-time">${timeRanges}</span></div>`;
                }
              });
              scheduleHTML += '</div>';
              hoursEl.innerHTML = scheduleHTML;
            }
          } else {
            hoursEl.innerHTML = '<span class="hours-unavailable">Hours not available</span>';
          }
        } catch (e) {
          console.error('[FIS] Hours parsing error:', e);
          hoursEl.innerHTML = '<span class="hours-unavailable">Hours not available</span>';
        }
      }
      
      // Set Google Maps directions link — prefer google_my_business_map_link if available
      const directionsLink = clone.querySelector('[data-fis-select]');
      const directionsUrl = store.google_my_business_map_link ||
        (store.latitude && store.longitude
          ? `https://www.google.com/maps/search/?api=1&query=${store.latitude},${store.longitude}`
          : null);
      if (directionsLink && directionsUrl) {
        directionsLink.href = directionsUrl;
        directionsLink.target = '_blank';
        directionsLink.rel = 'noopener noreferrer';
      }
      
      storeList.appendChild(clone);
    });
  }

  function updatePDPSection(section, store) {
    const content = section.querySelector('[data-fis-content]');
    const unavailable = section.querySelector('[data-fis-unavailable]');
    const loading = section.querySelector('[data-fis-loading]');
    const storeName = section.querySelector('[data-fis-store-name]');
    const storeAddress = section.querySelector('[data-fis-store-address]');
    const badge = section.querySelector('[data-fis-badge]');
    const stockStatus = section.querySelector('[data-fis-stock-status]');
    
    if (!content) return;
    
    loading?.classList.add('fis-hidden');
    
    if (store) {
      // Show the section with store data
      section.classList.remove('fis-hidden');
      content.classList.remove('fis-hidden');
      unavailable?.classList.add('fis-hidden');
      
      if (storeName) storeName.textContent = store.name;
      if (stockStatus) {
        stockStatus.textContent = store.statusLabel || 'In Stock';
      }
      if (storeAddress) {
        const addressText = [
          store.city,
          store.region
        ].filter(Boolean).join(', ');
        storeAddress.textContent = addressText;
      }
      if (badge) badge.textContent = store.statusLabel ? `(${store.statusLabel})` : '';
    } else {
      // Hide the entire section when no stores available
      // This is better UX than showing "not available"
      console.log('[Find in Store] No stores available for this variant - hiding section');
      section.classList.add('fis-hidden');
      content.classList.add('fis-hidden');
      unavailable?.classList.add('fis-hidden');
    }
  }

  function populateStateFilter(modal, stores) {
    const stateFilter = modal.querySelector('[data-fis-state-filter]');
    if (!stateFilter) return;
    
    const states = [...new Set(stores.map(s => s.region))].filter(Boolean).sort();
    
    while (stateFilter.options.length > 1) {
      stateFilter.remove(1);
    }
    
    states.forEach(state => {
      const option = document.createElement('option');
      option.value = state;
      option.textContent = state;
      stateFilter.appendChild(option);
    });
  }

  // ========================================
  // Inventory Fetching via Liquid Section Endpoint
  // ========================================
  
  async function fetchInventoryDataForVariant(variantId, productHandle) {
    // First, try to get inventory from embedded data on the page
    const embeddedData = getEmbeddedInventoryData(variantId);
    
    // Only use embedded data if it has ACTUAL location data (not fallback/empty)
    if (embeddedData && !embeddedData._fallback && !embeddedData._empty && !embeddedData._error) {
      console.log('[Find in Store] Using embedded inventory data for variant:', variantId);
      return embeddedData;
    }
    
    // If embedded data exists but has no locations, we need to fetch via AJAX
    // This happens because Shopify only populates store_availabilities for the initially selected variant
    if (embeddedData && (embeddedData._fallback || embeddedData._empty)) {
      console.log('[Find in Store] Embedded data has no locations, trying full page fetch for:', variantId);
    }
    
    // IMPORTANT: Shopify's section rendering does NOT populate store_availabilities
    // We need to fetch the FULL product page with ?variant=ID to get the data
    // The page render will have the requested variant selected, populating its store_availabilities
    try {
      const url = `${window.Shopify.routes.root}products/${productHandle}?variant=${variantId}`;
      
      console.log('[Find in Store] Fetching full product page for variant inventory:', url);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/html'
        }
      });
      
      if (!response.ok) {
        console.warn('[Find in Store] Product page fetch failed:', response.status);
        if (embeddedData) {
          console.log('[Find in Store] Falling back to embedded data');
          return embeddedData;
        }
        return { _error: true };
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Look for the embedded inventory data in the fetched page
      const dataEl = doc.querySelector('[data-fis-all-inventory]');
      if (dataEl) {
        try {
          const allVariantsData = JSON.parse(dataEl.textContent || '{}');
          const variantData = allVariantsData[variantId];
          
          if (variantData) {
            console.log('[Find in Store] ✅ Got inventory from full page for variant:', variantId, variantData.title || '');
            
            const locationsData = variantData.locations;
            const inventoryMap = {};
            
            if (locationsData && typeof locationsData === 'object') {
              for (const [locationId, locationData] of Object.entries(locationsData)) {
                const normalizedId = String(locationId).trim();
                if (!normalizedId) continue;
                inventoryMap[normalizedId] = locationData.quantity || (locationData.available ? 5 : 0);
                console.log(`[FIS] Location "${normalizedId}": qty=${inventoryMap[normalizedId]}`);
              }
            }
            
            console.log('[Find in Store] Inventory map from page:', inventoryMap, '(', Object.keys(inventoryMap).length, 'locations)');
            
            // If got actual location data, return it
            if (Object.keys(inventoryMap).length > 0) {
              // Update the embedded data on current page so we don't fetch again
              updateEmbeddedInventoryData(variantId, inventoryMap, variantData);
              return inventoryMap;
            }
            
            // No locations but variant is available
            if (variantData.available) {
              console.log('[Find in Store] ⚠️ Variant available but no pickup locations in fetched page');
              return { _fallback: true, _available: true, _quantity: variantData.inventory_quantity || 0 };
            }
          } else {
            console.warn('[Find in Store] Variant not found in fetched page data');
          }
        } catch (parseError) {
          console.error('[Find in Store] Error parsing fetched page data:', parseError);
        }
      } else {
        console.warn('[Find in Store] No embedded inventory data in fetched page');
      }
      
      // Fall back to embedded data if available
      if (embeddedData) {
        console.log('[Find in Store] Falling back to embedded data');
        return embeddedData;
      }
      
      return { _empty: true, _reason: 'no_data_in_fetched_page' };
    } catch (error) {
      console.error('[Find in Store] Failed to fetch inventory data:', error);
      // Fall back to embedded data if available
      if (embeddedData) {
        console.log('[Find in Store] Falling back to embedded data');
        return embeddedData;
      }
      return { _error: true };
    }
  }
  
  /**
   * Get inventory data from embedded JSON on the page
   * This is more reliable than fetching from variant endpoint
   */
  function getEmbeddedInventoryData(variantId) {
    // Try new format first (data-fis-all-inventory), then old format
    let dataEl = document.querySelector('[data-fis-all-inventory]');
    let isNewFormat = true;
    
    if (!dataEl) {
      dataEl = document.querySelector('[data-fis-variants-inventory]');
      isNewFormat = false;
    }
    
    if (!dataEl) {
      console.log('[Find in Store] No embedded inventory data found');
      return null;
    }
    
    try {
      const allVariantsData = JSON.parse(dataEl.textContent || '{}');
      const variantData = allVariantsData[variantId];
      
      if (!variantData) {
        console.warn('[Find in Store] Variant', variantId, 'not found in embedded data');
        console.log('[Find in Store] Available variant IDs:', Object.keys(allVariantsData));
        return null;
      }
      
      console.log('[Find in Store] ✅ Found embedded data for variant:', variantId, variantData.title || '');
      
      // Handle locations data - note that Shopify's Liquid store_availabilities
      // doesn't expose actual quantity per location, only 'available' boolean.
      // We use 'available' to determine status: available=true means In Stock.
      const locationsData = isNewFormat ? variantData.locations : variantData.store_availabilities;
      const inventoryMap = {};
      
      if (locationsData && typeof locationsData === 'object') {
        for (const [locationId, locationData] of Object.entries(locationsData)) {
          // Normalize location ID to string
          const normalizedId = String(locationId).trim();
          if (!normalizedId) continue;
          
          // Since Shopify doesn't expose per-location quantity via Liquid,
          // we use the 'available' boolean to set status:
          // - available=true → qty=IN_STOCK (will show "In Stock")
          // - available=false → qty=0 (will show "Out of Stock")
          let qty;
          if (locationData.available === true) {
            qty = INVENTORY_THRESHOLDS.IN_STOCK;
          } else {
            qty = 0;
          }
          
          inventoryMap[normalizedId] = qty;
          console.log(`[FIS] Location "${normalizedId}": available=${locationData.available}, mapped qty=${qty}`);
        }
      }
      
      console.log('[Find in Store] Inventory map:', inventoryMap, '(', Object.keys(inventoryMap).length, 'locations)');
      
      // If no store availabilities but variant is available, use fallback
      if (Object.keys(inventoryMap).length === 0 && variantData.available) {
        console.log('[Find in Store] ⚠️ No store availabilities, but variant is available - using fallback');
        return { 
          _fallback: true, 
          _available: true, 
          _quantity: variantData.inventory_quantity || 0 
        };
      }
      
      // If no store availabilities and variant not available
      if (Object.keys(inventoryMap).length === 0) {
        console.log('[Find in Store] No store availabilities and variant not available');
        return { _empty: true };
      }
      
      return inventoryMap;
    } catch (error) {
      console.error('[Find in Store] Error parsing embedded inventory data:', error);
      return null;
    }
  }

  /**
   * Update embedded inventory data with fetched data
   * This caches the data so we don't re-fetch for the same variant
   */
  function updateEmbeddedInventoryData(variantId, inventoryMap, variantData) {
    const dataEl = document.querySelector('[data-fis-all-inventory]');
    if (!dataEl) return;
    
    try {
      const allVariantsData = JSON.parse(dataEl.textContent || '{}');
      
      // Update the variant's location data
      if (allVariantsData[variantId]) {
        allVariantsData[variantId].locations = {};
        for (const [locationId, qty] of Object.entries(inventoryMap)) {
          allVariantsData[variantId].locations[locationId] = {
            available: qty > 0,
            quantity: qty
          };
        }
        
        // Update the JSON in the DOM
        dataEl.textContent = JSON.stringify(allVariantsData);
        console.log('[Find in Store] ✅ Cached inventory data for variant:', variantId);
      }
    } catch (error) {
      console.warn('[Find in Store] Could not cache inventory data:', error);
    }
  }

  // ========================================
  // Main Controller Class
  // ========================================
  
  class FindInStoreController {
    constructor() {
      this.modal = document.querySelector('[data-fis-modal]');
      this.section = document.querySelector('[data-fis-section]');
      
      if (!this.modal || !this.section) {
        console.log('[Find in Store] Required elements not found');
        return;
      }

      // Load threshold and message settings from section data attributes
      loadSettingsFromSection(this.section);
      
      this.allStores = [];
      this.filteredStores = [];
      this.inventoryData = {};
      this.userLocation = null;
      this.selectedStore = null;
      this.currentVariantId = null;
      
      this.init();
    }
    
    async init() {
      // Try to get product handle from modal first, then from section
      this.productHandle = this.modal.querySelector('[data-fis-product-handle]')?.value || 
                          this.section.querySelector('[data-fis-product-handle]')?.value;
      
      // Try to get variant ID from modal first, then from section
      this.currentVariantId = this.modal.querySelector('[data-fis-variant-id]')?.value || 
                             this.section.querySelector('[data-fis-current-variant]')?.value;
      
      if (!this.productHandle || !this.currentVariantId) {
        console.warn('[Find in Store] Missing product handle or variant ID');
        console.warn('Product handle:', this.productHandle, 'Variant ID:', this.currentVariantId);
        return;
      }
      
      console.log('[Find in Store] Initialized with product:', this.productHandle, 'variant:', this.currentVariantId);
      
      this.setupEventListeners();
      await this.loadData(this.currentVariantId);
      
      const savedStoreId = getStorageWithExpiry(STORAGE_KEY);
      if (savedStoreId) {
        const savedStore = this.filteredStores.find(s => s.id === savedStoreId);
        if (savedStore) {
          this.selectStore(savedStore);
        } else {
          this.showDefaultStores();
        }
      } else {
        this.showDefaultStores();
      }
    }
    
    async loadData(variantId) {
      try {
        // Hide content and unavailable, show loading
        this.section.querySelector('[data-fis-loading]')?.classList.remove('fis-hidden');
        this.section.querySelector('[data-fis-content]')?.classList.add('fis-hidden');
        this.section.querySelector('[data-fis-unavailable]')?.classList.add('fis-hidden');
        
        // Fetch stores first
        const stores = await fetchStores();
        this.allStores = stores;
        
        console.log('[Find in Store] Loaded', stores.length, 'stores');
        
        // Always fetch inventory data for quantity information
        const inventoryData = await fetchInventoryDataForVariant(variantId, this.productHandle);
        this.inventoryData = inventoryData;
        
        // Check if StoreAvailability API is accessible
        // If it returns data, pickup functionality is automatically enabled
        console.log('[Find in Store] Checking if StoreAvailability API is enabled...');
        const storeAvailabilityData = await fetchStoreAvailabilityForVariant(variantId);
        
        if (storeAvailabilityData.enabled) {
          console.log('[Find in Store] ✅ StoreAvailability API ENABLED - using API method');
          console.log('[Find in Store] → No dependency on Shopify Admin "Pickup in Store" settings');
          // Pass inventory data for quantity-based status
          this.filteredStores = filterStoresWithStoreAvailability(stores, storeAvailabilityData, inventoryData);
          this.usingStoreAvailabilityAPI = true;
        } else {
          // Fallback to old inventory method if API not accessible
          console.log('[Find in Store] ⚠️ StoreAvailability API not accessible - using fallback inventory method');
          this.filteredStores = filterStoresWithInventory(stores, inventoryData);
          this.usingStoreAvailabilityAPI = false;
        }
        
        console.log('[Find in Store] Final filtered stores:', this.filteredStores.length);
        populateStateFilter(this.modal, this.filteredStores);
        
      } catch (error) {
        console.error('[Find in Store] Failed to load data:', error);
        this.section.querySelector('[data-fis-loading]')?.classList.add('fis-hidden');
        this.section.querySelector('[data-fis-content]')?.classList.add('fis-hidden');
        this.section.querySelector('[data-fis-unavailable]')?.classList.remove('fis-hidden');
      }
    }
    
    setupEventListeners() {
      const openButton = this.section.querySelector('[data-fis-open-modal]');
      openButton?.addEventListener('click', () => this.openModal());
      
      const closeButton = this.modal.querySelector('[data-fis-close]');
      const backdrop = this.modal.querySelector('[data-fis-backdrop]');
      
      closeButton?.addEventListener('click', () => this.closeModal());
      backdrop?.addEventListener('click', () => this.closeModal());
      
      const searchInput = this.modal.querySelector('[data-fis-search]');
      searchInput?.addEventListener('input', (e) => this.handleSearch(e.target.value));
      
      const stateFilter = this.modal.querySelector('[data-fis-state-filter]');
      stateFilter?.addEventListener('change', (e) => this.handleStateFilter(e.target.value));
      
      const geoButton = this.modal.querySelector('[data-fis-geo]');
      geoButton?.addEventListener('click', () => this.handleGeoLocation());
      
      const storeList = this.modal.querySelector('[data-fis-store-list]');
      storeList?.addEventListener('click', (e) => {
        const selectButton = e.target.closest('[data-fis-select]');
        if (selectButton) {
          const storeItem = selectButton.closest('.fis-store-item');
          const storeId = storeItem?.dataset.storeId;
          if (storeId) {
            const store = this.filteredStores.find(s => s.id === storeId);
            if (store) {
              this.selectStore(store);
              this.closeModal();
            }
          }
        }
      });
      
      // Listen for variant changes - multiple event types
      document.addEventListener('variant:change', (e) => {
        if (e.detail?.variant?.id) {
          console.log('[Find in Store] variant:change event detected:', e.detail.variant.id);
          this.handleVariantChange(e.detail.variant.id);
        }
      });
      
      document.addEventListener('swym:variant-change', (e) => {
        if (e.detail) {
          console.log('[Find in Store] swym:variant-change event detected:', e.detail);
          this.handleVariantChange(e.detail);
        }
      });
      
      document.addEventListener('size-variant-changed', () => {
        // When size changes, get the variant from a data attribute or form
        const variantIdEl = document.querySelector('[data-product-variant-id]') || 
                           document.querySelector('.product-variant-id') ||
                           document.querySelector('[name="id"]');
        if (variantIdEl) {
          const newVariantId = variantIdEl.value || variantIdEl.dataset.productVariantId;
          if (newVariantId && newVariantId !== this.currentVariantId) {
            console.log('[Find in Store] size-variant-changed event detected:', newVariantId);
            this.handleVariantChange(newVariantId);
          }
        }
      });
      
      // Observer for variant input field changes
      const variantInputSelectors = [
        '.product-variant-id',
        '[name="id"]',
        '[data-product-variant-id]',
        'input[type="hidden"][name="id"]'
      ];
      
      for (const selector of variantInputSelectors) {
        const variantInput = document.querySelector(selector);
        if (variantInput) {
          console.log('[Find in Store] Watching variant input:', selector);
          const observer = new MutationObserver(() => {
            const newVariantId = variantInput.value || variantInput.dataset.productVariantId;
            if (newVariantId && newVariantId !== this.currentVariantId) {
              console.log('[Find in Store] Variant input changed:', newVariantId);
              this.handleVariantChange(newVariantId);
            }
          });
          observer.observe(variantInput, { 
            attributes: true, 
            attributeFilter: ['value', 'data-product-variant-id'] 
          });
          break; // Only watch the first one found
        }
      }
    }
    
    async handleVariantChange(newVariantId) {
      this.currentVariantId = newVariantId;
      
      const variantInput = this.modal.querySelector('[data-fis-variant-id]');
      if (variantInput) variantInput.value = newVariantId;
      
      // Hide all content, show loading
      this.section.querySelector('[data-fis-loading]')?.classList.remove('fis-hidden');
      this.section.querySelector('[data-fis-content]')?.classList.add('fis-hidden');
      this.section.querySelector('[data-fis-unavailable]')?.classList.add('fis-hidden');
      
      // Always fetch inventory data for quantity information
      this.inventoryData = await fetchInventoryDataForVariant(newVariantId, this.productHandle);
      
      // Check if StoreAvailability API is accessible for this variant
      const storeAvailabilityData = await fetchStoreAvailabilityForVariant(newVariantId);
      
      if (storeAvailabilityData.enabled) {
        console.log('[Find in Store] ✅ StoreAvailability API enabled for variant change');
        // Pass inventory data for quantity-based status
        this.filteredStores = filterStoresWithStoreAvailability(this.allStores, storeAvailabilityData, this.inventoryData);
        this.usingStoreAvailabilityAPI = true;
      } else {
        console.log('[Find in Store] ⚠️ Using fallback inventory method for variant change');
        this.filteredStores = filterStoresWithInventory(this.allStores, this.inventoryData);
        this.usingStoreAvailabilityAPI = false;
      }
      
      if (this.selectedStore) {
        const stillAvailable = this.filteredStores.find(s => s.id === this.selectedStore.id);
        if (stillAvailable) {
          this.selectStore(stillAvailable);
        } else {
          this.selectedStore = null;
          this.showDefaultStores();
        }
      } else {
        this.showDefaultStores();
      }
    }
    
    showDefaultStores() {
      const defaultStores = this.filteredStores.slice(0, DEFAULT_STORES_TO_SHOW);
      
      if (defaultStores.length > 0) {
        updatePDPSection(this.section, defaultStores[0]);
        this.selectedStore = defaultStores[0];
      } else {
        // No stores available - hide content and show unavailable message
        this.section.querySelector('[data-fis-loading]')?.classList.add('fis-hidden');
        this.section.querySelector('[data-fis-content]')?.classList.add('fis-hidden');
        this.section.querySelector('[data-fis-unavailable]')?.classList.remove('fis-hidden');
        this.selectedStore = null;
      }
    }
    
    selectStore(store) {
      this.selectedStore = store;
      updatePDPSection(this.section, store);
      setStorageWithExpiry(STORAGE_KEY, store.id, STORAGE_EXPIRY_DAYS);
      
      const storeItems = this.modal.querySelectorAll('.fis-store-item');
      storeItems.forEach(item => {
        const button = item.querySelector('[data-fis-select]');
        if (item.dataset.storeId === store.id) {
          item.classList.add('selected');
          if (button) button.textContent = 'Selected';
        } else {
          item.classList.remove('selected');
          if (button) button.textContent = 'Select this store';
        }
      });
    }
    
    openModal() {
      const drawer = this.modal.querySelector('[data-fis-drawer]');
      const backdrop = this.modal.querySelector('[data-fis-backdrop]');
      
      drawer?.classList.remove('fis-hidden');
      backdrop?.classList.remove('fis-hidden');
      
      setTimeout(() => {
        drawer?.classList.add('fis-active');
        backdrop?.classList.add('fis-active');
      }, 10);
      
      // Apply current filters (preserve state from last session)
      let storesToShow = this.filteredStores;
      
      const stateFilter = this.modal.querySelector('[data-fis-state-filter]');
      if (stateFilter?.value) {
        storesToShow = filterStoresByState(storesToShow, stateFilter.value);
      }
      
      const searchInput = this.modal.querySelector('[data-fis-search]');
      if (searchInput?.value) {
        storesToShow = filterStoresBySearch(storesToShow, searchInput.value);
      }
      
      if (this.userLocation) {
        storesToShow = sortStoresByDistance(storesToShow, this.userLocation);
      }
      
      renderStoreList(this.modal, storesToShow, this.selectedStore?.id);
      
      document.body.style.overflow = 'hidden';
    }
    
    closeModal() {
      const drawer = this.modal.querySelector('[data-fis-drawer]');
      const backdrop = this.modal.querySelector('[data-fis-backdrop]');
      
      drawer?.classList.remove('fis-active');
      backdrop?.classList.remove('fis-active');
      
      setTimeout(() => {
        drawer?.classList.add('fis-hidden');
        backdrop?.classList.add('fis-hidden');
      }, 300);
      
      document.body.style.overflow = '';
    }
    
    handleSearch(query) {
      let stores = filterStoresBySearch(this.filteredStores, query);
      
      const stateFilter = this.modal.querySelector('[data-fis-state-filter]');
      if (stateFilter?.value) {
        stores = filterStoresByState(stores, stateFilter.value);
      }
      
      if (this.userLocation) {
        stores = sortStoresByDistance(stores, this.userLocation);
      }
      
      renderStoreList(this.modal, stores, this.selectedStore?.id);
    }
    
    handleStateFilter(stateCode) {
      let stores = filterStoresByState(this.filteredStores, stateCode);
      
      const searchInput = this.modal.querySelector('[data-fis-search]');
      if (searchInput?.value) {
        stores = filterStoresBySearch(stores, searchInput.value);
      }
      
      if (this.userLocation) {
        stores = sortStoresByDistance(stores, this.userLocation);
      }
      
      renderStoreList(this.modal, stores, this.selectedStore?.id);
    }
    
    async handleGeoLocation() {
      const geoButton = this.modal.querySelector('[data-fis-geo]');
      if (!geoButton) return;
      
      geoButton.disabled = true;
      geoButton.innerHTML = '<span>Getting location...</span>';
      
      try {
        this.userLocation = await getUserLocation();
        
        let stores = sortStoresByDistance(this.filteredStores, this.userLocation);
        
        const searchInput = this.modal.querySelector('[data-fis-search]');
        if (searchInput?.value) {
          stores = filterStoresBySearch(stores, searchInput.value);
        }
        
        const stateFilter = this.modal.querySelector('[data-fis-state-filter]');
        if (stateFilter?.value) {
          stores = filterStoresByState(stores, stateFilter.value);
        }
        
        renderStoreList(this.modal, stores, this.selectedStore?.id);
        
        geoButton.disabled = false;
        geoButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 0C5.24 0 3 2.24 3 5c0 3.38 5 11 5 11s5-7.62 5-11c0-2.76-2.24-5-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" fill="currentColor"/>
          </svg>
          <span>Sorted by distance</span>
        `;
      } catch (error) {
        console.error('[Find in Store] Geolocation error:', error);
        alert('Unable to access your location. Please enable location services and try again.');
        geoButton.disabled = false;
        geoButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 0C5.24 0 3 2.24 3 5c0 3.38 5 11 5 11s5-7.62 5-11c0-2.76-2.24-5-5-5zm0 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" fill="currentColor"/>
          </svg>
          <span>Near me</span>
        `;
      }
    }
  }

  // ========================================
  // Initialize
  // ========================================
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new FindInStoreController();
    });
  } else {
    new FindInStoreController();
  }
})();
